import Vue from 'vue';
import Router from 'vue-router';
import authentication from '@/authentication';
import screens from '@/screens';
import store from '@/store';
import user from '@/components/user';
import i18n from '@/configured/vue-i18n';
import log from '@/configured/logger';
import offline from '@/offline';
import { verifySession } from '@/api';
import { loadBaseDataIfNeeded } from '@/base-data';
import {
  createBootstrapContext,
  hasCompletedBootstrapWindow,
} from '@/base-data/shared-load';

Vue.use(Router);

const router = new Router({
  routes: [...screens.routes],
});

router.beforeEach(beforeEach);
router.afterEach(afterEach);

export default router;

//////////

/** Increments on every completed navigation; stable hook for Playwright / e2e "route settled". */
let pkpRouteNavGeneration = 0;

/**
 * Exposes deterministic route state on `<html>` for automation (translation scan, smoke tests).
 * Applied **synchronously** in `afterEach` so attributes exist as soon as Vue Router commits the
 * navigation (Playwright was racing `$nextTick` + `requestAnimationFrame` and timing out). Route
 * components may still load data async — tests should wait on route-specific UI when needed.
 *
 * - `data-pkp-route` — Vue route name (e.g. `ProductDetail`)
 * - `data-pkp-path` — `to.path` (e.g. `/product/9587-acme` after canonical slug redirect) — compare to hash path
 * - `data-pkp-nav-generation` — increases each navigation; use when path might repeat
 */
function setPkpRouteDocumentMarkers(to) {
  pkpRouteNavGeneration += 1;
  const el = document.documentElement;
  el.setAttribute('data-pkp-route', to.name || '');
  el.setAttribute('data-pkp-path', to.path || '');
  el.setAttribute('data-pkp-nav-generation', String(pkpRouteNavGeneration));
}

function updatePageTitle(to) {
  const translationKey = `pageTitle${to.name}`;
  const pageTitle = i18n.i18n.t(`pageTitle${to.name}`);
  const divider = i18n.i18n.t(`pageTitleDivider`);
  const siteName = i18n.i18n.t('siteName');
  if (pageTitle === translationKey) {
    document.title = siteName;
  } else {
    document.title = pageTitle + divider + siteName;
  }
}

async function beforeEach(to, from, next) {
  try {
    if (to.meta.isPublic) {
      next();
      return;
    }
    if (!store.state.authentication.isLoggedIn) {
      log.info('Not logged in, redirecting to login');
      if (to.name === 'Login') {
        next();
      } else {
        if (to.name === 'Home') {
          next({ name: 'Login' });
        } else {
          next({ name: 'Login', query: { redirect: to.path } });
        }
      }
      return;
    }
    log.info('Running route guards');
    const rejected = await guardAuth(to);
    if (rejected) {
      throw rejected;
    }
    log.info('Routing', to, from);
    const bootstrapContext = createBootstrapContext({ mode: 'startup' });
    if (!hasCompletedBootstrapWindow(bootstrapContext)) {
      await loadBaseDataIfNeeded({ bootstrapContext });
    }
    next();
  } catch (reason) {
    log.error('Rejecting route', to, 'due to', reason);
    if (reason === 403) {
      next({ name: 'Home' });
    } else {
      next({ name: 'Error', replace: true });
    }
  }
}

function afterEach(to, from) {
  if (router.app) {
    router.app.$emit('closeNavigation');
  }
  // OneTrust handles analytics consent - no custom logic needed
  updatePageTitle(to);
  if (
    !store.state[offline.downloadStore.namespace].downloadInitialized &&
    store.state[offline.downloadStore.namespace].offlineModeEnabled &&
    store.state.authentication.isLoggedIn
  ) {
    store.dispatch(`${offline.downloadStore.namespace}/initCache`);
  }
  setPkpRouteDocumentMarkers(to);
}

function isTrainingEnabled() {
  if ('user/region' in store.getters) {
    return store.getters['user/region'].trainingEnabled;
  }
  return false;
}

// Check if a potential route path is the current path
// or the parent of the current path.
function isRouteOrParentOfRoute(to, routePath) {
  if (to.path === routePath) {
    return true;
  }

  if (typeof to.matched[0] === 'undefined') {
    return false;
  }

  if (to.matched[0].path === routePath) {
    // We are in a child path of the route path.
    return true;
  }

  return false;
}

function isOutboxEnabled() {
  if ('user/region' in store.getters) {
    return store.getters['user/region'].outboxEnabled;
  }
  return false;
}

function guardAuth(to) {
  log.debug('Starting guardAuth');
  verifySession(true);

  if (to.meta.isPublic) {
    log.debug('Public route. ¯\\_(ツ)_/¯');
    return Promise.resolve();
  }

  const loggedIn = store.state[authentication.store.namespace].isLoggedIn;
  const currentUser = store.state[user.store.namespace].currentUser;
  const loaded = 'id' in currentUser;
  const approved = 'approved' in currentUser && currentUser.approved;
  const valid = loggedIn && loaded && approved;

  // Use the "name" property here because it is consistent for all outbox routes.
  if (valid && to.name === 'Outbox' && !isOutboxEnabled()) {
    return Promise.reject(403);
  }

  if (
    valid &&
    isRouteOrParentOfRoute(to, '/training') &&
    !isTrainingEnabled()
  ) {
    return Promise.reject(403);
  }

  if (valid) {
    log.debug('Guard allows Valid user to enter');
    return Promise.resolve();
  }

  if (loggedIn && !loaded) {
    // Let the getCurrentUser method resolve a private page
    log.info(
      'User is logged in but not loaded. Attempting load before rejection.'
    );

    return store
      .dispatch(`${user.store.namespace}/getCurrentUser`, {
        bootstrapContext: createBootstrapContext({ mode: 'startup' }),
      })
      .then((r) => guardAuth(to))
      .catch((e) => {
        log.warn('Could not get current user. logging out.');
        store.dispatch(`${authentication.store.namespace}/logOut`);
        throw e;
      });
  }

  if (to.meta.allowUnapproved && loggedIn && loaded) {
    log.debug(
      'Allowing access to a semi-private screen for an unapproved account.'
    );
    return Promise.resolve();
  }

  log.warn('Invalid route request.', { loggedIn, loaded, approved, to });
  return Promise.reject(403);
}
